/**
 * useSummon Hook
 *
 * Phase 6-2: カード召喚・チャージ処理を担当するカスタムフック
 *
 * 担当する関数:
 * - summonCard: モンスター/魔法/フィールド/フェイズカードの召喚・発動
 * - chargeCard: 手札カードをモンスターにチャージ
 * - chargeSP: SPトークンをモンスターにチャージ
 * - chargePhaseCard: 手札カードをフェイズカードにチャージ
 */

import React, { useCallback } from 'react';
import { createMonsterInstance } from '../../utils/helpers';
import { calculateSummonCost } from '../../engine/summonCost';
import { statusEffectEngine } from '../../engine/statusEffects';
import { continuousEffectEngine } from '../../engine/continuousEffects';
import { registerCardTriggers, fireTrigger, fireOptionalTriggers, hasCardTrigger } from '../../engine/triggerEngine';
import { TRIGGER_TYPES } from '../../engine/triggerTypes';
import { executeSkillEffects } from '../../engine/effectEngine';
import { getPhaseCardStageText, getStageName } from '../../engine/phaseCardEffects';

import type { MonsterInstance, CardData, HandCard, FieldCardInstance, PhaseCardInstance, CardWithId } from '../../types/card';
import type { PlayerNumber } from '../../types/player';
import type { PendingMonsterTarget, PendingDeckReview, PendingGraveyardSelection, PendingHandSelection } from '../../types/ui';
import type { PlayerStatusEffect } from '../../engine/statusEffects/statusEngine';
import type { DeepSeaZone } from '../../engine/deepSeaZone/types';

/** useSummon 依存オブジェクト */
interface UseSummonDeps {
  // ゲーム状態（読み取り）
  currentPlayer: PlayerNumber;
  summoningSickness: boolean;
  chargeUsedThisTurn: boolean;

  // プレイヤー状態（読み取り）
  p1Field: (MonsterInstance | null)[];
  p2Field: (MonsterInstance | null)[];
  p1Hand: HandCard[];
  p2Hand: HandCard[];
  p1Deck: CardData[];
  p2Deck: CardData[];
  p1Graveyard: CardData[];
  p2Graveyard: CardData[];
  p1Life: number;
  p2Life: number;
  p1ActiveSP: number;
  p2ActiveSP: number;
  p1RestedSP: number;
  p2RestedSP: number;
  p1BonusSP: number;
  p2BonusSP: number;
  p1StatusEffects: PlayerStatusEffect[];
  p2StatusEffects: PlayerStatusEffect[];
  p1PhaseCard: PhaseCardInstance | null;
  p2PhaseCard: PhaseCardInstance | null;
  p1MagicBlocked: boolean;
  p2MagicBlocked: boolean;

  // 深海領域
  p1DeepSeaZone: DeepSeaZone;
  p2DeepSeaZone: DeepSeaZone;

  // Setters
  setP1Life: React.Dispatch<React.SetStateAction<number>>;
  setP2Life: React.Dispatch<React.SetStateAction<number>>;
  setP1Field: React.Dispatch<React.SetStateAction<(MonsterInstance | null)[]>>;
  setP2Field: React.Dispatch<React.SetStateAction<(MonsterInstance | null)[]>>;
  setP1Hand: React.Dispatch<React.SetStateAction<HandCard[]>>;
  setP2Hand: React.Dispatch<React.SetStateAction<HandCard[]>>;
  setP1Deck: React.Dispatch<React.SetStateAction<CardData[]>>;
  setP2Deck: React.Dispatch<React.SetStateAction<CardData[]>>;
  setP1Graveyard: React.Dispatch<React.SetStateAction<CardData[]>>;
  setP2Graveyard: React.Dispatch<React.SetStateAction<CardData[]>>;
  setP1ActiveSP: React.Dispatch<React.SetStateAction<number>>;
  setP2ActiveSP: React.Dispatch<React.SetStateAction<number>>;
  setP1RestedSP: React.Dispatch<React.SetStateAction<number>>;
  setP2RestedSP: React.Dispatch<React.SetStateAction<number>>;
  setP1BonusSP: React.Dispatch<React.SetStateAction<number>>;
  setP2BonusSP: React.Dispatch<React.SetStateAction<number>>;
  setP1FieldCard: React.Dispatch<React.SetStateAction<FieldCardInstance | null>>;
  setP2FieldCard: React.Dispatch<React.SetStateAction<FieldCardInstance | null>>;
  setP1PhaseCard: React.Dispatch<React.SetStateAction<PhaseCardInstance | null>>;
  setP2PhaseCard: React.Dispatch<React.SetStateAction<PhaseCardInstance | null>>;
  setP1NextTurnSPBonus: React.Dispatch<React.SetStateAction<number>>;
  setP2NextTurnSPBonus: React.Dispatch<React.SetStateAction<number>>;
  setChargeUsedThisTurn: React.Dispatch<React.SetStateAction<boolean>>;
  setP1DeepSeaZone: React.Dispatch<React.SetStateAction<DeepSeaZone>>;
  setP2DeepSeaZone: React.Dispatch<React.SetStateAction<DeepSeaZone>>;

  // UI状態Setters
  setPendingMonsterTarget: React.Dispatch<React.SetStateAction<PendingMonsterTarget | null>>;
  setPendingDeckReview: React.Dispatch<React.SetStateAction<PendingDeckReview | null>>;
  setPendingGraveyardSelection: React.Dispatch<React.SetStateAction<PendingGraveyardSelection | null>>;
  setPendingHandSelection: React.Dispatch<React.SetStateAction<PendingHandSelection | null>>;
  setShowGraveyardViewer: React.Dispatch<React.SetStateAction<boolean>>;

  // ユーティリティ
  addLog: (message: string, type?: string) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: (action: any) => void;
}

/** useSummon 戻り値 */
interface UseSummonReturn {
  summonCard: (card: HandCard, slotIndex: number) => boolean;
  chargeCard: (card: HandCard, monsterIndex: number) => boolean;
  chargeSP: (monsterIndex: number) => boolean;
  chargePhaseCard: (card: HandCard) => boolean;
}

/**
 * @param deps - 依存関係オブジェクト
 * @returns { summonCard, chargeCard, chargeSP, chargePhaseCard }
 */
export const useSummon = (deps: UseSummonDeps): UseSummonReturn => {
  const {
    // ゲーム状態（読み取り）
    currentPlayer,
    summoningSickness,
    chargeUsedThisTurn,

    // プレイヤー状態（読み取り）
    p1Field,
    p2Field,
    p1Hand,
    p2Hand,
    p1Deck,
    p2Deck,
    p1Graveyard,
    p2Graveyard,
    p1Life,
    p2Life,
    p1ActiveSP,
    p2ActiveSP,
    p1RestedSP,
    p2RestedSP,
    p1BonusSP,
    p2BonusSP,
    p1StatusEffects,
    p2StatusEffects,
    p1PhaseCard,
    p2PhaseCard,
    p1MagicBlocked,
    p2MagicBlocked,

    // 深海領域
    p1DeepSeaZone,
    p2DeepSeaZone,

    // Setters
    setP1Life,
    setP2Life,
    setP1Field,
    setP2Field,
    setP1Hand,
    setP2Hand,
    setP1Deck,
    setP2Deck,
    setP1Graveyard,
    setP2Graveyard,
    setP1ActiveSP,
    setP2ActiveSP,
    setP1RestedSP,
    setP2RestedSP,
    setP1BonusSP,
    setP2BonusSP,
    setP1FieldCard,
    setP2FieldCard,
    setP1PhaseCard,
    setP2PhaseCard,
    setP1NextTurnSPBonus,
    setP2NextTurnSPBonus,
    setChargeUsedThisTurn,
    setP1DeepSeaZone,
    setP2DeepSeaZone,

    // UI状態Setters
    setPendingMonsterTarget,
    setPendingDeckReview,
    setPendingGraveyardSelection,
    setPendingHandSelection,
    setShowGraveyardViewer,

    // ユーティリティ
    addLog,
    dispatch,
  } = deps;

  // =============================================================================
  // chargeCard: 手札カードをモンスターにチャージ
  // =============================================================================
  const chargeCard = useCallback((card: HandCard, monsterIndex: number): boolean => {
    if (chargeUsedThisTurn) {
      addLog('このターンは既にチャージを使用しました', 'damage');
      return false;
    }

    const field = currentPlayer === 1 ? p1Field : p2Field;
    const monster = field[monsterIndex];

    if (!monster) {
      addLog('モンスターが存在しません', 'damage');
      return false;
    }

    if (monster.charges && monster.charges.length >= 2) {
      addLog('このモンスターは既に2枚チャージされています', 'damage');
      return false;
    }

    // 属性チャージ（モンスター、魔法、フィールドカード、フェーズカード）
    if (card.type === 'monster' || card.type === 'magic' || card.type === 'field' || card.type === 'phasecard') {
      const newCharge = {
        card: card,
        type: 'attribute',
        attribute: card.attribute,
      };

      if (currentPlayer === 1) {
        setP1Field(prev => {
          const newField = [...prev];
          newField[monsterIndex] = {
            ...monster,
            charges: [...(monster.charges || []), newCharge],
          };
          return newField;
        });
        setP1Hand(prev => prev.filter(c => c.uniqueId !== card.uniqueId));
      } else {
        setP2Field(prev => {
          const newField = [...prev];
          newField[monsterIndex] = {
            ...monster,
            charges: [...(monster.charges || []), newCharge],
          };
          return newField;
        });
        setP2Hand(prev => prev.filter(c => c.uniqueId !== card.uniqueId));
      }

      setChargeUsedThisTurn(true);
      addLog(`${monster.name}に${card.name}をチャージしました`, 'info');

      return true;
    }

    addLog('チャージできるのは属性カードのみです', 'damage');
    return false;
  }, [currentPlayer, p1Field, p2Field, chargeUsedThisTurn, addLog,
      setP1Field, setP2Field, setP1Hand, setP2Hand, setChargeUsedThisTurn]);

  // =============================================================================
  // chargeSP: SPトークンをモンスターにチャージ
  // =============================================================================
  const chargeSP = useCallback((monsterIndex: number): boolean => {
    if (chargeUsedThisTurn) {
      addLog('このターンは既にチャージを使用しました', 'damage');
      return false;
    }

    const field = currentPlayer === 1 ? p1Field : p2Field;
    const activeSP = currentPlayer === 1 ? p1ActiveSP : p2ActiveSP;
    const monster = field[monsterIndex];

    if (!monster) {
      addLog('モンスターが存在しません', 'damage');
      return false;
    }

    if (monster.charges && monster.charges.length >= 2) {
      addLog('このモンスターは既に2枚チャージされています', 'damage');
      return false;
    }

    if (activeSP < 1) {
      addLog('SPが足りません', 'damage');
      return false;
    }

    // SPチャージを追加
    const spCharge: { card?: CardWithId; type: string } = {
      card: undefined,
      type: 'sp',
    };

    const setField = currentPlayer === 1 ? setP1Field : setP2Field;
    const setActiveSP = currentPlayer === 1 ? setP1ActiveSP : setP2ActiveSP;

    setField(prev => {
      const newField = [...prev];
      newField[monsterIndex] = {
        ...monster,
        charges: [...(monster.charges || []), spCharge],
      };
      return newField;
    });

    // SP総数を減らす（restedSPには加算しない = 永久消費）
    setActiveSP(prev => prev - 1);
    setChargeUsedThisTurn(true);

    addLog(`${monster.name}にSPトークンをチャージ`, 'info');

    return true;
  }, [currentPlayer, p1Field, p2Field, p1ActiveSP, p2ActiveSP, chargeUsedThisTurn, addLog,
      setP1Field, setP2Field, setP1ActiveSP, setP2ActiveSP, setChargeUsedThisTurn]);

  // =============================================================================
  // chargePhaseCard: 手札カードをフェイズカードにチャージ
  // =============================================================================
  const chargePhaseCard = useCallback((card: HandCard): boolean => {
    if (chargeUsedThisTurn) {
      addLog('このターンは既にチャージを使用しました', 'damage');
      return false;
    }

    const phaseCard = currentPlayer === 1 ? p1PhaseCard : p2PhaseCard;
    const setPhaseCard = currentPlayer === 1 ? setP1PhaseCard : setP2PhaseCard;
    const setHand = currentPlayer === 1 ? setP1Hand : setP2Hand;
    const setGraveyard = currentPlayer === 1 ? setP1Graveyard : setP2Graveyard;

    if (!phaseCard || phaseCard.type !== 'phasecard') {
      addLog('フェイズカードが設置されていません', 'damage');
      return false;
    }

    // 現在の段階を取得（chargesの数、または stageフィールド）
    const currentStage = phaseCard.stage !== undefined ? phaseCard.stage : (phaseCard.charges?.length || 0);

    // 最大段階チェック（3枚チャージで最終段階）
    if (currentStage >= 3) {
      addLog('フェイズカードは既に最終段階です', 'damage');
      return false;
    }

    // 属性チェック（同属性のみ、または「なし」属性のカードも可）
    if (card.attribute !== phaseCard.attribute && card.attribute !== 'なし' && phaseCard.attribute !== 'なし') {
      addLog(`フェイズカードと同じ属性のカードのみチャージできます（フェイズカード: ${phaseCard.attribute}）`, 'damage');
      return false;
    }

    // 属性カードのみチャージ可能（フェイズカードも含む）
    if (card.type !== 'monster' && card.type !== 'magic' && card.type !== 'field' && card.type !== 'phasecard') {
      addLog('チャージできるのは属性カード（モンスター、魔法、フィールド、フェイズ）のみです', 'damage');
      return false;
    }

    // 新しいチャージを追加
    const newCharge: { card?: CardWithId; type: string } = {
      card: card,
      type: 'phase_charge',
    };

    const newStage = currentStage + 1;
    const updatedPhaseCard = {
      ...phaseCard,
      stage: newStage,
      charges: [...(phaseCard.charges || []), newCharge],
    };

    // 手札から削除
    setHand(prev => prev.filter(c => c.uniqueId !== card.uniqueId));

    // 段階名を取得（新しい関数を使用）
    const stageName = getStageName(newStage);
    addLog(`フェイズカード【${phaseCard.name}】に${card.name}をチャージ → 【${stageName}】（${newStage}/3）`, 'info');

    // CSVの効果テキストから段階効果を取得
    const stageEffectText = getPhaseCardStageText(phaseCard, newStage);

    if (stageEffectText) {
      // 段階効果のテキストをログに表示
      addLog(`【${stageName}効果】: ${stageEffectText}`, 'info');
    }

    // 段階変更時にON_PHASE_CARD_ACTIVATEトリガーを発火
    // 深海領域コンテキストを含むトリガーコンテキストを作成
    const myDeepSeaZone = currentPlayer === 1 ? p1DeepSeaZone : p2DeepSeaZone;
    const setMyDeepSeaZone = currentPlayer === 1 ? setP1DeepSeaZone : setP2DeepSeaZone;
    const triggerContext = {
      currentPlayer,
      card: updatedPhaseCard,
      slotIndex: null,
      stage: newStage,  // 新しい段階
      p1Field,
      p2Field,
      p1Hand,
      p2Hand,
      p1Deck,
      p2Deck,
      p1Graveyard,
      p2Graveyard,
      p1ActiveSP,
      p2ActiveSP,
      p1RestedSP,
      p2RestedSP,
      p1Life,
      p2Life,
      setP1Life,
      setP2Life,
      setP1Field,
      setP2Field,
      setP1Hand,
      setP2Hand,
      setP1Deck,
      setP2Deck,
      setP1Graveyard,
      setP2Graveyard,
      setP1ActiveSP,
      setP2ActiveSP,
      setP1RestedSP,
      setP2RestedSP,
      myDeepSeaZone,
      setMyDeepSeaZone,
      p1DeepSeaZone,
      p2DeepSeaZone,
      setP1DeepSeaZone,
      setP2DeepSeaZone,
      myPhaseCard: updatedPhaseCard,  // 更新後のフェイズカード
      setPendingMonsterTarget,
      setPendingDeckReview,
      setPendingGraveyardSelection,
      setPendingHandSelection,
      addLog,
      summoningSickness,
      turn: 0,  // turnはこのフックでは利用不可
    };

    // 段階効果トリガーを発火
    fireTrigger(TRIGGER_TYPES.ON_PHASE_CARD_ACTIVATE, triggerContext as any);

    // 最終段階（3枚チャージ）の場合は墓地へ送る
    if (newStage >= 3) {
      addLog(`フェイズカード【${phaseCard.name}】は最終段階の効果を発動し、墓地へ送られます`, 'info');

      // 最終段階効果を実行
      const stage3EffectText = getPhaseCardStageText(phaseCard, 3);
      if (stage3EffectText) {
        const stage3Context = {
          currentPlayer,
          monsterIndex: null,
          p1Life,
          p2Life,
          setP1Life,
          setP2Life,
          setP1Field,
          setP2Field,
          setP1Hand,
          setP2Hand,
          setP1Deck,
          setP2Deck,
          setP1Graveyard,
          setP2Graveyard,
          p1Field,
          p2Field,
          p1Hand,
          p2Hand,
          p1Deck,
          p2Deck,
          p1Graveyard,
          p2Graveyard,
          p1ActiveSP,
          p2ActiveSP,
          setP1ActiveSP,
          setP2ActiveSP,
          p1RestedSP,
          p2RestedSP,
          setP1RestedSP,
          setP2RestedSP,
          addLog,
          summoningSickness,
        };
        // 「3枚重ね: 」プレフィックスを付けて効果実行
        executeSkillEffects(`3枚重ね: ${stage3EffectText}`, stage3Context as any, phaseCard.id);
      }

      // フェイズカードの常時効果を解除
      continuousEffectEngine.unregister(phaseCard.uniqueId);

      // フェイズカードとチャージされたカードを全て墓地へ
      const chargedCards = updatedPhaseCard.charges.map(c => {
        // チャージオブジェクトの場合は.cardを、CardDataの場合はそのまま返す
        return 'card' in c && c.card ? c.card : c as CardData;
      }).filter((c): c is CardData => c !== undefined);
      const cardsToGraveyard = [
        { ...updatedPhaseCard, charges: [] }, // フェイズカード本体（chargesは分離）
        ...chargedCards,
      ];

      setGraveyard(prev => [...prev, ...cardsToGraveyard]);
      setPhaseCard(null);
    } else {
      // フェイズカードの常時効果を新しい段階に更新
      continuousEffectEngine.updatePhaseCardStage(updatedPhaseCard, currentPlayer, newStage);

      setPhaseCard(updatedPhaseCard);
    }

    setChargeUsedThisTurn(true);
    return true;
  }, [currentPlayer, p1PhaseCard, p2PhaseCard, p1Field, p2Field, p1Hand, p2Hand,
      p1Deck, p2Deck, p1Graveyard, p2Graveyard, p1ActiveSP, p2ActiveSP,
      p1RestedSP, p2RestedSP, p1Life, p2Life, chargeUsedThisTurn, addLog, summoningSickness,
      setP1Life, setP2Life, setP1Field, setP2Field, setP1Hand, setP2Hand,
      setP1Deck, setP2Deck, setP1Graveyard, setP2Graveyard,
      setP1ActiveSP, setP2ActiveSP, setP1RestedSP, setP2RestedSP,
      setP1PhaseCard, setP2PhaseCard, setChargeUsedThisTurn,
      p1DeepSeaZone, p2DeepSeaZone, setP1DeepSeaZone, setP2DeepSeaZone,
      setPendingMonsterTarget, setPendingDeckReview, setPendingGraveyardSelection, setPendingHandSelection]);

  // =============================================================================
  // summonCard: カード召喚（モンスター/魔法/フィールド/フェイズカード）
  // =============================================================================
  const summonCard = useCallback((card: HandCard, slotIndex: number): boolean => {
    // 現在のプレイヤーのSPを直接取得
    const activeSP = currentPlayer === 1 ? p1ActiveSP : p2ActiveSP;
    const field = currentPlayer === 1 ? p1Field : p2Field;

    // カード種別に応じたコスト軽減を計算
    let actualCost = card.cost;
    let costModifierSource = null;
    const costContext = {
      currentPlayer,
      effectOwner: currentPlayer,
      p1Field,
      p2Field,
      p1Life,
      p2Life,
    };

    if (card.type === 'monster') {
      // 常時効果からのコスト修正
      const { modifier, sources } = continuousEffectEngine.getSummonCostModifierDetails(card as any, currentPlayer, costContext);
      // カード固有の一時的コスト修正（潮の乙女など）
      const tempModifier = card.tempCostModifier || 0;
      const tempSource = card.tempCostModifierSource || null;

      // プレイヤー状態異常からのコスト軽減（クリスタルブレス等）
      const myStatusEffects = currentPlayer === 1 ? p1StatusEffects : p2StatusEffects;
      const statusCostReduction = statusEffectEngine.getSummonCostReduction(myStatusEffects, card.attribute);

      const totalModifier = modifier + tempModifier - statusCostReduction;
      actualCost = calculateSummonCost(card.cost, modifier, tempModifier, statusCostReduction);

      const allSources = [...sources];
      if (tempSource) {
        allSources.push(tempSource);
      }
      if (statusCostReduction > 0) {
        allSources.push('光輝の祝福');
      }
      if (totalModifier !== 0 && allSources.length > 0) {
        costModifierSource = allSources.join(', ');
      }
    } else if (card.type === 'magic') {
      // 魔法カードのコスト軽減を計算（エリザヴェット等の効果）
      const magicModifier = continuousEffectEngine.calculateMagicCostModifier(card as any, currentPlayer, costContext);

      // C0000552: 海を舐めた末路 - 深海領域のカード枚数分コスト軽減
      let deepSeaReduction = 0;
      if (card.id === 'C0000552') {
        const playerDeepSeaZone = currentPlayer === 1 ? p1DeepSeaZone : p2DeepSeaZone;
        if (playerDeepSeaZone && playerDeepSeaZone.isUnlocked) {
          deepSeaReduction = playerDeepSeaZone.cards.length;
        }
      }

      const totalMagicModifier = magicModifier - deepSeaReduction;
      if (totalMagicModifier !== 0) {
        actualCost = Math.max(0, card.cost + totalMagicModifier);
        const sources: string[] = [];
        if (deepSeaReduction > 0) {
          sources.push('深海領域');
        }
        if (magicModifier !== 0) {
          sources.push('効果');
        }
        costModifierSource = sources.length > 0 ? sources.join(', ') : '効果';
      }
    }

    // SPチェック（activeSP + bonusSP）
    const bonusSP = currentPlayer === 1 ? p1BonusSP : p2BonusSP;
    const totalAvailableSP = activeSP + bonusSP;
    if (totalAvailableSP < actualCost) {
      addLog(`SPが足りません！（必要: ${actualCost}, 現在: ${activeSP}${bonusSP > 0 ? `+☆${bonusSP}` : ''}）`, 'damage');
      return false;
    }

    // SP消費計算（activeSP優先、足りなければbonusSP使用）
    const fromActive = Math.min(activeSP, actualCost);
    const fromBonus = actualCost - fromActive;

    if (card.type === 'monster') {
      if (field[slotIndex] !== null) {
        addLog('そのスロットは使用中です', 'damage');
        return false;
      }

      const monsterInstance = createMonsterInstance(card);
      monsterInstance.canAttack = !summoningSickness; // 召喚酔いOFFなら即攻撃可能
      monsterInstance.owner = currentPlayer; // 常時効果のターゲット判定用

      // フィールドにモンスターを配置
      // 「次の1体のみ」のコスト軽減を使用した場合、他のカードからリセット
      const usedOneTimeModifier = card.tempCostModifierOneTime && card.tempCostModifier;
      const oneTimeSource = usedOneTimeModifier ? card.tempCostModifierSource : null;

      if (currentPlayer === 1) {
        setP1Field(prev => {
          const newField = [...prev];
          newField[slotIndex] = monsterInstance;
          return newField;
        });
        // 手札からカードを削除し、同じソースの「次の1体のみ」軽減をリセット
        setP1Hand(prev => prev.filter(c => c.uniqueId !== card.uniqueId).map(c => {
          if (oneTimeSource && c.tempCostModifierOneTime && c.tempCostModifierSource === oneTimeSource) {
            const { tempCostModifier, tempCostModifierSource, tempCostModifierOneTime, ...rest } = c;
            return rest;
          }
          return c;
        }));
        // SP消費（activeSP優先、足りなければbonusSP使用）
        setP1ActiveSP(prev => prev - fromActive);
        setP1RestedSP(prev => prev + fromActive); // レスト化はactiveSP分のみ
        if (fromBonus > 0) {
          setP1BonusSP(prev => prev - fromBonus);
          addLog(`☆ボーナスSPを${fromBonus}使用！`, 'heal');
        }
      } else {
        setP2Field(prev => {
          const newField = [...prev];
          newField[slotIndex] = monsterInstance;
          return newField;
        });
        // 手札からカードを削除し、同じソースの「次の1体のみ」軽減をリセット
        setP2Hand(prev => prev.filter(c => c.uniqueId !== card.uniqueId).map(c => {
          if (oneTimeSource && c.tempCostModifierOneTime && c.tempCostModifierSource === oneTimeSource) {
            const { tempCostModifier, tempCostModifierSource, tempCostModifierOneTime, ...rest } = c;
            return rest;
          }
          return c;
        }));
        // SP消費（activeSP優先、足りなければbonusSP使用）
        setP2ActiveSP(prev => prev - fromActive);
        setP2RestedSP(prev => prev + fromActive); // レスト化はactiveSP分のみ
        if (fromBonus > 0) {
          setP2BonusSP(prev => prev - fromBonus);
          addLog(`☆ボーナスSPを${fromBonus}使用！`, 'heal');
        }
      }

      // コスト軽減があった場合はログに表示
      if (costModifierSource && actualCost < card.cost) {
        addLog(`プレイヤー${currentPlayer}: ${card.name}を召喚！（${costModifierSource}により召喚コスト${card.cost}→${actualCost}）`, 'info');
      } else {
        addLog(`プレイヤー${currentPlayer}: ${card.name}を召喚！`, 'info');
      }

      // トリガーを登録
      registerCardTriggers(monsterInstance, currentPlayer, slotIndex);

      // 常時効果を登録
      continuousEffectEngine.register(monsterInstance, currentPlayer);

      // 召喚時バフを適用（他のカードの常時効果による）
      const summonBuffContext = {
        currentPlayer,
        effectOwner: currentPlayer,
        p1Field: currentPlayer === 1 ? [...p1Field.slice(0, slotIndex), monsterInstance, ...p1Field.slice(slotIndex + 1)] : p1Field,
        p2Field: currentPlayer === 2 ? [...p2Field.slice(0, slotIndex), monsterInstance, ...p2Field.slice(slotIndex + 1)] : p2Field,
        p1Life,
        p2Life,
      };
      const summonBuffs = continuousEffectEngine.getSummonBuffs(monsterInstance as any, currentPlayer, summonBuffContext);
      if (summonBuffs.hpBuff > 0) {
        monsterInstance.currentHp += summonBuffs.hpBuff;
        monsterInstance.maxHp = (monsterInstance.maxHp || monsterInstance.currentHp) + summonBuffs.hpBuff;
        addLog(`${monsterInstance.name}のHPが${summonBuffs.hpBuff}アップ！（常時効果）`, 'info');
      }
      if (summonBuffs.atkBuff > 0) {
        monsterInstance.currentAttack += summonBuffs.atkBuff;
        addLog(`${monsterInstance.name}の攻撃力が${summonBuffs.atkBuff}アップ！（常時効果）`, 'info');
      }

      // 召喚時効果を実行（新表記【召喚時】と旧表記「召喚時」に対応）
      // ただし、トリガーシステムに実装済みの場合はスキップ（二重実行を防ぐ）
      const hasTriggerImplementation = hasCardTrigger(card.id, TRIGGER_TYPES.ON_SUMMON);

      if (card.effect && (card.effect.includes('召喚時') || card.effect.includes('【召喚時】')) && !hasTriggerImplementation) {
        addLog(`${card.name}の召喚時効果発動！`, 'info');

        // 召喚時効果を含む全テキストを渡す（カード固有処理で判定）
        const context = {
          currentPlayer,
          monsterIndex: slotIndex,
          card: monsterInstance,
          setP1Life,
          setP2Life,
          setP1Field,
          setP2Field,
          setP1Hand,
          setP2Hand,
          setP1Deck,
          setP2Deck,
          setP1Graveyard,
          setP2Graveyard,
          setP1ActiveSP,
          setP2ActiveSP,
          setP1RestedSP,
          setP2RestedSP,
          p1Field,
          p2Field,
          p1Hand,
          p2Hand,
          p1Deck,
          p2Deck,
          p1Graveyard,
          p2Graveyard,
          p1ActiveSP,
          p2ActiveSP,
          p1RestedSP,
          p2RestedSP,
          addLog,
          setPendingMonsterTarget,
          setPendingDeckReview,
          setPendingGraveyardSelection,
          setPendingHandSelection,
        };
        // カードIDを渡して効果を実行
        executeSkillEffects(card.effect, context as any, card.id);
      }

      // 召喚時トリガーを発火（トリガーシステムに登録された効果を実行）
      // 重要: 召喚後のフィールド状態を作成（React state更新は非同期のため、古い状態を渡すとトリガー内で問題が発生する）
      const updatedP1Field = currentPlayer === 1
        ? [...p1Field.slice(0, slotIndex), monsterInstance, ...p1Field.slice(slotIndex + 1)]
        : p1Field;
      const updatedP2Field = currentPlayer === 2
        ? [...p2Field.slice(0, slotIndex), monsterInstance, ...p2Field.slice(slotIndex + 1)]
        : p2Field;

      const triggerContext = {
        currentPlayer,
        card: monsterInstance,
        slotIndex,
        monsterIndex: slotIndex, // トリガー効果で使用
        setP1Life,
        setP2Life,
        setP1Field,
        setP2Field,
        setP1Hand,
        setP2Hand,
        setP1Deck,
        setP2Deck,
        setP1Graveyard,
        setP2Graveyard,
        setP1ActiveSP,
        setP2ActiveSP,
        setP1RestedSP,
        setP2RestedSP,
        p1Field: updatedP1Field,
        p2Field: updatedP2Field,
        p1Hand,
        p2Hand,
        p1Deck,
        p2Deck,
        p1Graveyard,
        p2Graveyard,
        p1Life,
        p2Life,
        p1ActiveSP,
        p2ActiveSP,
        p1RestedSP,
        p2RestedSP,
        addLog,
        setPendingHandSelection,
        setPendingGraveyardSelection,
        setShowGraveyardViewer,
        setPendingDeckReview,
        setPendingMonsterTarget,
        summoningSickness, // 分裂したモンスターの召喚酔い設定に必要
      };
      fireTrigger(TRIGGER_TYPES.ON_SUMMON, triggerContext as any);

      return true;
    }

    if (card.type === 'magic') {
      // 魔法カード使用制限チェック（触覚持ち粘液獣等）
      const isMagicBlocked = currentPlayer === 1 ? p1MagicBlocked : p2MagicBlocked;
      if (isMagicBlocked) {
        addLog('魔法カードを使用できません！（触覚持ち粘液獣の効果）', 'damage');
        return false;
      }

      // コスト軽減は既に冒頭で計算済み（actualCost, costModifierSource）
      // SPチェックは冒頭で既に実施済み（actualCostで判定）

      // SP消費計算（activeSP優先、足りなければbonusSP使用）
      const magicFromActive = Math.min(activeSP, actualCost);
      const magicFromBonus = actualCost - magicFromActive;

      if (currentPlayer === 1) {
        setP1Hand(prev => prev.filter(c => c.uniqueId !== card.uniqueId));
        setP1ActiveSP(prev => prev - magicFromActive);
        setP1RestedSP(prev => prev + magicFromActive);
        if (magicFromBonus > 0) {
          setP1BonusSP(prev => prev - magicFromBonus);
          addLog(`☆ボーナスSPを${magicFromBonus}使用！`, 'heal');
        }
        setP1Graveyard(prev => [...prev, card]);
      } else {
        setP2Hand(prev => prev.filter(c => c.uniqueId !== card.uniqueId));
        setP2ActiveSP(prev => prev - magicFromActive);
        setP2RestedSP(prev => prev + magicFromActive);
        if (magicFromBonus > 0) {
          setP2BonusSP(prev => prev - magicFromBonus);
          addLog(`☆ボーナスSPを${magicFromBonus}使用！`, 'heal');
        }
        setP2Graveyard(prev => [...prev, card]);
      }

      if (costModifierSource) {
        addLog(`プレイヤー${currentPlayer}: ${card.name}を発動！（コスト${card.cost}→${actualCost}）`, 'info');
      } else {
        addLog(`プレイヤー${currentPlayer}: ${card.name}を発動！`, 'info');
      }

      // 魔法効果を実行
      if (card.effect) {
        // 深海領域のコンテキストを現在のプレイヤーに応じて設定
        const myDeepSeaZone = currentPlayer === 1 ? p1DeepSeaZone : p2DeepSeaZone;
        const setMyDeepSeaZone = currentPlayer === 1 ? setP1DeepSeaZone : setP2DeepSeaZone;

        const context = {
          currentPlayer,
          monsterIndex: null, // 魔法カードはモンスターではない
          // 魔障壁判定用フラグ
          isMagicEffect: true,
          magicCaster: currentPlayer,
          setP1Life,
          setP2Life,
          setP1Field,
          setP2Field,
          setP1Hand,
          setP2Hand,
          setP1Deck,
          setP2Deck,
          setP1Graveyard,
          setP2Graveyard,
          p1Field,
          p2Field,
          p1Hand,
          p2Hand,
          p1Deck,
          p2Deck,
          p1Graveyard,
          p2Graveyard,
          addLog,
          setPendingDeckReview,
          setPendingMonsterTarget,
          // 墓地選択（クラーケンの呼び声等）
          setPendingGraveyardSelection,
          setShowGraveyardViewer,
          // SPボーナス設定（マーメイドの恵み等）
          setP1NextTurnSPBonus,
          setP2NextTurnSPBonus,
          // 深海領域コンテキスト（レヴィアタンの大時化等）
          myDeepSeaZone,
          setMyDeepSeaZone,
        };
        executeSkillEffects(card.effect, context as any, card.id);
      }

      // 自分のON_SELF_MAGIC_ACTIVATEDトリガーを発火（呪核の杖・ザルヴェリオ等のフェイズカード効果）
      const selfMagicTriggerContext = {
        currentPlayer,                 // トリガー発動者は自分
        card,                          // 発動された魔法カード
        p1Field,
        p2Field,
        p1Life,
        p2Life,
        setP1Life,
        setP2Life,
        setP1Field,
        setP2Field,
        p1ActiveSP,
        p2ActiveSP,
        p1RestedSP,
        p2RestedSP,
        setP1ActiveSP,
        setP2ActiveSP,
        setP1RestedSP,
        setP2RestedSP,
        p1PhaseCard,
        p2PhaseCard,
        addLog,
      };
      fireTrigger(TRIGGER_TYPES.ON_SELF_MAGIC_ACTIVATED, selfMagicTriggerContext as any);

      // 相手のON_OPPONENT_MAGIC_ACTIVATEDトリガーを発火（マグマ・ドラゴン等）
      const opponentPlayer = currentPlayer === 1 ? 2 : 1;
      const opponentMagicTriggerContext = {
        currentPlayer: opponentPlayer, // トリガー発動者は相手
        magicCard: card,               // 発動された魔法カード
        p1Field,
        p2Field,
        p1Life,
        p2Life,
        setP1Life,
        setP2Life,
        setP1Field,
        setP2Field,
        p1ActiveSP,
        p2ActiveSP,
        addLog,
      };
      fireTrigger(TRIGGER_TYPES.ON_OPPONENT_MAGIC_ACTIVATED, opponentMagicTriggerContext as any);

      return true;
    }

    if (card.type === 'field') {
      // フィールドカードにowner情報を追加
      const fieldCardInstance = { ...card, owner: currentPlayer };

      if (currentPlayer === 1) {
        setP1FieldCard(fieldCardInstance);
        setP1Hand(prev => prev.filter(c => c.uniqueId !== card.uniqueId));
        setP1ActiveSP(prev => prev - card.cost);
        setP1RestedSP(prev => prev + card.cost);
      } else {
        setP2FieldCard(fieldCardInstance);
        setP2Hand(prev => prev.filter(c => c.uniqueId !== card.uniqueId));
        setP2ActiveSP(prev => prev - card.cost);
        setP2RestedSP(prev => prev + card.cost);
      }

      // トリガーを登録
      registerCardTriggers(fieldCardInstance, currentPlayer, null);

      // 常時効果を登録
      continuousEffectEngine.register(fieldCardInstance, currentPlayer);

      addLog(`プレイヤー${currentPlayer}: ${card.name}を設置！`, 'info');

      // フィールドカード設置時トリガーを発火（深海領域開放など）
      const myDeepSeaZone = currentPlayer === 1 ? p1DeepSeaZone : p2DeepSeaZone;
      const setMyDeepSeaZone = currentPlayer === 1 ? setP1DeepSeaZone : setP2DeepSeaZone;
      const fieldCardContext = {
        currentPlayer,
        card: fieldCardInstance,
        monsterIndex: null,
        setP1Life,
        setP2Life,
        setP1Field,
        setP2Field,
        setP1Hand,
        setP2Hand,
        setP1Deck,
        setP2Deck,
        setP1Graveyard,
        setP2Graveyard,
        p1Field,
        p2Field,
        p1Hand,
        p2Hand,
        p1Deck,
        p2Deck,
        p1Graveyard,
        p2Graveyard,
        p1ActiveSP,
        p2ActiveSP,
        p1RestedSP,
        p2RestedSP,
        setP1ActiveSP,
        setP1RestedSP,
        setP2ActiveSP,
        setP2RestedSP,
        addLog,
        // 深海領域コンテキスト
        myDeepSeaZone,
        setMyDeepSeaZone,
      };
      fireTrigger(TRIGGER_TYPES.ON_FIELD_CARD_SET, fieldCardContext as any);
      fireOptionalTriggers(TRIGGER_TYPES.ON_FIELD_CARD_SET, fieldCardContext as any);

      return true;
    }

    if (card.type === 'phasecard') {
      // フェイズカードに段階情報とチャージ配列とowner情報を追加
      const initializedPhaseCard: PhaseCardInstance = {
        ...card,
        stage: 0,           // 初期段階
        stackCount: 0,      // 重ね枚数
        charges: [],        // チャージされたカード
        owner: currentPlayer, // 常時効果のターゲット判定用
      };

      if (currentPlayer === 1) {
        setP1PhaseCard(initializedPhaseCard);
        setP1Hand(prev => prev.filter(c => c.uniqueId !== card.uniqueId));
        setP1ActiveSP(prev => prev - card.cost);
        setP1RestedSP(prev => prev + card.cost);
      } else {
        setP2PhaseCard(initializedPhaseCard);
        setP2Hand(prev => prev.filter(c => c.uniqueId !== card.uniqueId));
        setP2ActiveSP(prev => prev - card.cost);
        setP2RestedSP(prev => prev + card.cost);
      }

      addLog(`プレイヤー${currentPlayer}: フェイズカード【${card.name}】を設置！【初期段階】`, 'info');

      // フェイズカードの初期効果を実行
      // 深海領域のコンテキストを現在のプレイヤーに応じて設定
      const myDeepSeaZone = currentPlayer === 1 ? p1DeepSeaZone : p2DeepSeaZone;
      const setMyDeepSeaZone = currentPlayer === 1 ? setP1DeepSeaZone : setP2DeepSeaZone;
      const context = {
        currentPlayer,
        card: initializedPhaseCard, // 発動元のフェイズカード（トリガーフィルタリング用）
        monsterIndex: null,
        setP1Life,
        setP2Life,
        setP1Field,
        setP2Field,
        setP1Hand,
        setP2Hand,
        setP1Deck,
        setP2Deck,
        setP1Graveyard,
        setP2Graveyard,
        p1Field,
        p2Field,
        p1Hand,
        p2Hand,
        p1Deck,
        p2Deck,
        p1Graveyard,
        p2Graveyard,
        p1ActiveSP,
        p2ActiveSP,
        setP1ActiveSP,
        setP2ActiveSP,
        p1RestedSP,
        p2RestedSP,
        setP1RestedSP,
        setP2RestedSP,
        addLog,
        summoningSickness,
        // 深海領域コンテキスト
        myDeepSeaZone,
        setMyDeepSeaZone,
        setPendingHandSelection,
        turn: 1, // NOTE: turnは呼び出し元から渡す必要がある場合は要修正
      };

      // CSVの効果テキストから初期効果を取得して表示
      const initialEffectText = getPhaseCardStageText(card, 0);
      if (initialEffectText) {
        addLog(`【初期効果】: ${initialEffectText}`, 'info');
      }

      // フェイズカードのトリガーを登録
      registerCardTriggers(initializedPhaseCard, currentPlayer, null);

      // フェイズカードの常時効果を登録（初期段階 stage=0）
      continuousEffectEngine.registerPhaseCard(initializedPhaseCard, currentPlayer, 0);

      // 【設置時】トリガーを発火（深海領域開放など）
      fireTrigger(TRIGGER_TYPES.ON_PHASE_CARD_SET, context as any);
      // 【設置時】OPTIONALトリガーを発火（水属性モンスター送りなど）
      fireOptionalTriggers(TRIGGER_TYPES.ON_PHASE_CARD_SET, context as any);

      // 【発動時】トリガーを発火
      fireTrigger(TRIGGER_TYPES.ON_PHASE_CARD_ACTIVATE, context as any);

      return true;
    }

    return false;
  }, [currentPlayer, p1ActiveSP, p2ActiveSP, p1RestedSP, p2RestedSP,
      p1Field, p2Field, p1Hand, p2Hand,
      p1Deck, p2Deck, p1Graveyard, p2Graveyard, p1Life, p2Life, addLog,
      p1StatusEffects, p2StatusEffects, p1BonusSP, p2BonusSP, p1PhaseCard, p2PhaseCard,
      p1MagicBlocked, p2MagicBlocked, summoningSickness,
      p1DeepSeaZone, p2DeepSeaZone,
      setP1Life, setP2Life, setP1Field, setP2Field, setP1Hand, setP2Hand,
      setP1Deck, setP2Deck, setP1Graveyard, setP2Graveyard,
      setP1ActiveSP, setP1RestedSP, setP2ActiveSP, setP2RestedSP,
      setP1BonusSP, setP2BonusSP, setP1FieldCard, setP2FieldCard,
      setP1PhaseCard, setP2PhaseCard, setP1NextTurnSPBonus, setP2NextTurnSPBonus,
      setP1DeepSeaZone, setP2DeepSeaZone,
      setPendingMonsterTarget, setPendingDeckReview, setPendingGraveyardSelection,
      setPendingHandSelection, setShowGraveyardViewer, dispatch]);

  return {
    summonCard,
    chargeCard,
    chargeSP,
    chargePhaseCard,
  };
};

export default useSummon;
